import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, Award, Heart, Check, Gift, Calendar, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

import { API } from "../config/api";

// Normalize slug: remove year suffix like -2026, -2025
function normalizeSlug(slug) {
  if (!slug) return slug;
  const m = slug.match(/^(.*?)-\d{4}$/);
  if (m) return m[1];
  return slug;
}

// Event configurations
const EVENT_CONFIG = {
  picnic: {
    title: "Annual Picnic 2026",
    displayTitle: "Event",
    date: "July 11, 2026",
    location: "London, Ontario"
  },
  onam: {
    title: "Onam 2026",
    displayTitle: "Event",
    date: "September 2026",
    location: "London, Ontario"
  },
  shivaratri: {
    title: "Shivaratri 2026",
    displayTitle: "Event",
    date: "February 2026",
    location: "London, Ontario"
  },
  ramanavami: {
    title: "Ramanavami 2026",
    displayTitle: "Event",
    date: "March 2026",
    location: "London, Ontario"
  }
};

// Sponsorship Tiers with detailed benefits
const SPONSORSHIP_TIERS = [
  {
    name: "Mega Sponsor",
    subtitle: "Exclusive - Only 1 Mega Sponsor",
    gradient: "bg-gradient-to-r from-yellow-500 via-orange-500 to-orange-400",
    bgColor: "bg-orange-50",
    benefits: [
      "Participate in the inauguration ceremony with the chief guest",
      "Two-minute on-stage address",
      "Branding on e-tickets and event wristbands",
      "Exclusive brand booth in the hallway",
      "Brand announced four times during key moments",
      "Frequent appearance on main projected screens",
      "Eight complimentary event tickets",
      "Up to 2-minute video advertisement on LED wall during inauguration",
      "Special token of appreciation from the chief guest",
      "Premium banners at main stage (1), ticket counter (1), dining area (2) and flag banners on school ground",
      "Leaflet distribution to attendees",
      "Digital flyer displayed four times on LED wall and used as footer display",
      "Paid social media promotions on Facebook, Instagram, YouTube and WhatsApp groups",
      "Honour of presenting the first ticket at the Ticket Launch Ceremony"
    ]
  },
  {
    name: "Elite Sponsor",
    subtitle: "",
    gradient: "bg-gradient-to-r from-purple-600 via-purple-500 to-violet-400",
    bgColor: "bg-purple-50",
    benefits: [
      "Stage recognition - Invited on stage during the inauguration ceremony",
      "Your brand name announced three times during key events",
      "Frequently displayed on projected screen",
      "Strategic banner placement - Hallways (2) and dining area (1)",
      "You will be invited on stage to present the appreciation award",
      "Brand formally introduced and you will be felicitated during a prime event",
      "Your digital flyer will be displayed three times on LED display",
      "Brochure and leaflet distribution - Direct marketing opportunity",
      "Standard paid social media promotions across Facebook, Instagram, YouTube and WhatsApp groups",
      "Six complimentary event tickets"
    ]
  },
  {
    name: "Diamond Sponsor",
    subtitle: "",
    gradient: "bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400",
    bgColor: "bg-blue-50",
    benefits: [
      "Stage recognition - Invited to stage during inauguration ceremony",
      "Your digital flyer will be displayed two times on LED display",
      "Strategic banner placement - Hallways (1) and dining area (1)",
      "You will be invited on stage to present the appreciation award",
      "Your brand name announced once during key events",
      "Frequently displayed on projected screen",
      "Basic paid social media promotions across Facebook, Instagram, YouTube and WhatsApp groups",
      "Four complimentary event tickets"
    ]
  },
  {
    name: "Gold Sponsor",
    subtitle: "",
    gradient: "bg-gradient-to-r from-yellow-700 via-yellow-600 to-yellow-500",
    bgColor: "bg-yellow-50",
    benefits: [
      "Stage recognition - Invited to stage during inauguration ceremony",
      "Frequently displayed on projected screen",
      "Two complimentary event tickets",
      "Your digital flyer will be displayed once on LED display",
      "Fair paid social media promotions across Facebook, Instagram, YouTube and WhatsApp groups",
      "You will participate in the appreciation handover during the event's conclusion"
    ]
  }
];

export default function SponsorshipEventPage() {
  const { eventSlug } = useParams();
  const navigate = useNavigate();
  const normalizedSlug = normalizeSlug(eventSlug);
  
  const eventConfig = EVENT_CONFIG[normalizedSlug] || {
    title: `${normalizedSlug?.charAt(0).toUpperCase()}${normalizedSlug?.slice(1)} 2026`,
    displayTitle: "Event",
    date: "TBA",
    location: "London, Ontario"
  };

  const [selectedTier, setSelectedTier] = useState(null);

  const handleSelectTier = (tierName) => {
    setSelectedTier(tierName);
    // Scroll to contact section or navigate
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    toast.success(`Selected ${tierName}! Please contact us to proceed.`);
  };

  const whySponsorItems = [
    {
      icon: Users,
      title: "Community Reach",
      description: "Connect with families in the Kerala Hindu community of London Ontario."
    },
    {
      icon: Award,
      title: "Brand Visibility",
      description: "Recognition through banners, social media, and event promotions."
    },
    {
      icon: Heart,
      title: "Community Impact",
      description: "Support cultural preservation and community bonding initiatives."
    }
  ];

  return (
    <div className="pt-20" data-testid="sponsorship-event-page">
      {/* Hero Section with gradient background */}
      <section 
        className="relative py-24 md:py-32"
        style={{
          background: 'linear-gradient(135deg, rgba(30, 70, 50, 0.95) 0%, rgba(45, 90, 65, 0.9) 50%, rgba(60, 110, 80, 0.85) 100%)',
        }}
      >
        {/* Background image overlay */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1532375810709-75b1da00537c?w=1920")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }}
        />
        
        <div className="relative section-container text-center">
          <p className="text-kerala-gold text-xs tracking-[0.3em] uppercase mb-6">
            SPONSORSHIP OPPORTUNITY
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6 italic">
            {eventConfig.displayTitle}
          </h1>
          <div className="flex items-center justify-center gap-2 text-white/90 mb-2">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">{eventConfig.date}</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-white/90">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{eventConfig.location}</span>
          </div>
        </div>
      </section>

      {/* Support This Event Section */}
      <section className="py-20 bg-kerala-cream">
        <div className="section-container text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-kerala-green mb-4 italic">
            Support This Event
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto mb-8 text-sm md:text-base">
            Your sponsorship or donation helps us create memorable cultural experiences for our 
            community. Partner with us to celebrate Kerala's rich heritage together.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button 
              className="bg-kerala-green hover:bg-kerala-green/90 text-white px-6 py-3 rounded-md flex items-center gap-2"
              onClick={() => document.getElementById('sponsorship-tiers')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="become-sponsor-btn"
            >
              <Award className="w-4 h-4" />
              Become a Sponsor
            </Button>
            <Button 
              variant="outline"
              className="border-kerala-green text-kerala-green hover:bg-kerala-green/5 px-6 py-3 rounded-md flex items-center gap-2"
              onClick={() => navigate('/contact')}
              data-testid="donate-btn"
            >
              <Heart className="w-4 h-4" />
              Donate
            </Button>
          </div>
        </div>
      </section>

      {/* Why Sponsor Section */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <h2 className="font-heading text-3xl md:text-4xl text-kerala-green text-center mb-16 italic">
            Why Sponsor?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-4xl mx-auto">
            {whySponsorItems.map((item, idx) => (
              <div key={idx} className="text-center" data-testid={`why-sponsor-${idx}`}>
                <div className="w-20 h-20 bg-kerala-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-kerala-gold" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl text-kerala-green mb-3 italic">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsorship Tiers Section */}
      <section id="sponsorship-tiers" className="py-20 bg-kerala-cream">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-kerala-green mb-4 italic">
              Sponsorship Tiers
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto text-sm md:text-base">
              Choose a sponsorship level that fits your organization. All sponsors receive recognition and 
              visibility at our events.
            </p>
          </div>

          <div className="space-y-12">
            {SPONSORSHIP_TIERS.map((tier, idx) => (
              <div key={idx} className="max-w-5xl mx-auto" data-testid={`tier-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}>
                {/* Tier Header */}
                <div className={`${tier.gradient} rounded-t-lg py-12 px-8 text-center`}>
                  <h3 className="font-heading text-3xl md:text-4xl text-white mb-2 italic">
                    {tier.name}
                  </h3>
                  {tier.subtitle && (
                    <p className="text-white/90 text-sm mb-4">
                      {tier.subtitle}
                    </p>
                  )}
                  <Button 
                    className="bg-white text-gray-800 hover:bg-gray-100 px-8 py-3 rounded-md font-medium"
                    onClick={() => handleSelectTier(tier.name)}
                    data-testid={`select-tier-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    Select This Tier
                  </Button>
                </div>

                {/* Benefits Section */}
                <div className={`${tier.bgColor} rounded-b-lg py-10 px-8`}>
                  <div className="flex items-center gap-2 mb-6">
                    <Gift className="w-5 h-5 text-kerala-green" />
                    <h4 className="font-heading text-lg text-kerala-green italic">
                      Benefits & Features
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                    {tier.benefits.map((benefit, bidx) => (
                      <div key={bidx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-kerala-green mt-0.5 shrink-0" />
                        <span className="text-gray-700 text-sm leading-relaxed">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="py-20 bg-white">
        <div className="section-container text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl text-kerala-green mb-4 italic">
            Ready to Sponsor?
          </h2>
          <p className="text-gray-600 mb-8">
            Contact us to discuss sponsorship opportunities and customize a package that works for your organization.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              className="bg-kerala-green hover:bg-kerala-green/90 text-white px-8 py-3 rounded-md"
              onClick={() => navigate('/contact')}
              data-testid="contact-us-btn"
            >
              Contact Us
            </Button>
            <a 
              href="mailto:tatvamasilondonontario@gmail.com" 
              className="text-kerala-green hover:text-kerala-green/80 underline"
            >
              tatvamasilondonontario@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
